import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isAuthenticated, logout } from '../../../services/authService';
import OptimizedLogo from '../../OptimizedImage/OptimizedLogo';
import styles from './Navbar.module.css';

const Navbar = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(isAuthenticated());
  const [userInfo, setUserInfo] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);

  // Fonction pour vérifier si l'utilisateur est admin
  const isAdmin = () => {
    return userInfo && userInfo.roles && userInfo.roles.includes('ROLE_ADMIN');
  };

  // Vérifie si l'utilisateur possède l'un des rôles fournis (ROLE_ADMIN a toujours accès)
  const hasAnyRole = (roles = []) => {
    if (isAdmin()) return true;
    const userRoles = (userInfo && userInfo.roles) || [];
    return roles.some((role) => userRoles.includes(role));
  };
  
  // Map de correspondance entre les codes de langue et les codes de fichiers de drapeaux
  const flagCodeMap = {
    'fr': 'fr',
    'fr-fr': 'fr',
    'en': 'gb',
    'en-us': 'gb',
    'en-gb': 'gb'
  };
  
  const [currentLang, setCurrentLang] = useState(() => {
    // Obtient le code de drapeau correct basé sur la langue actuelle
    const langCode = i18n.language.toLowerCase();
    const flagCode = flagCodeMap[langCode] || langCode.split('-')[0];
    
    return {
      code: i18n.language.split('-')[0].toUpperCase(),
      flag: `/images/flags/${flagCode}.svg`
    };
  });

  const languages = [
    { code: 'FR', name: 'Français', flag: '/images/flags/fr.svg' },
    { code: 'EN', name: 'English', flag: '/images/flags/gb.svg' }
  ];
  useEffect(() => {
    const checkAuth = () => {
      const isAuth = isAuthenticated();
      setIsLoggedIn(isAuth);
      if (isAuth) {
        const storedUserInfo = localStorage.getItem('userInfo');
        if (storedUserInfo) {
          setUserInfo(JSON.parse(storedUserInfo));
        }
      } else {
        setUserInfo(null);
      }
    };

    checkAuth();
    // Écouter les changements de localStorage depuis d'autres onglets
    window.addEventListener('storage', checkAuth);
    // Écouter l'événement personnalisé pour les changements dans le même onglet
    window.addEventListener('localStorageUpdated', checkAuth);
    
    return () => {
      window.removeEventListener('storage', checkAuth);
      window.removeEventListener('localStorageUpdated', checkAuth);
    };
  }, []);

  // UseEffect pour synchroniser la langue avec i18n
  useEffect(() => {
    const updateLanguageState = () => {
      const langCode = i18n.language.toLowerCase();
      const flagCode = flagCodeMap[langCode] || langCode.split('-')[0];
      
      setCurrentLang({
        code: i18n.language.split('-')[0].toUpperCase(),
        flag: `/images/flags/${flagCode}.svg`
      });
    };

    // Écouter les changements de langue
    i18n.on('languageChanged', updateLanguageState);
    
    // Mettre à jour l'état initial
    updateLanguageState();

    return () => {
      i18n.off('languageChanged', updateLanguageState);
    };
  }, [i18n]);

  const handleLanguageChange = (lang) => {
    const langCode = lang.code.toLowerCase();
    // Sauvegarder dans localStorage avant de changer
    localStorage.setItem('i18nextLng', langCode);
    i18n.changeLanguage(langCode);
    setCurrentLang({ code: lang.code, flag: lang.flag });
  };

  const handleLogout = () => {
    logout();
    setIsLoggedIn(false);
    setUserInfo(null);
    navigate('/');
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const handleMenuItemClick = (item) => {
    if (item.action === 'logout') {
      handleLogout();
    }
    closeMobileMenu();
  };

  const toggleDropdown = (menuLabel) => {
    setOpenDropdown(openDropdown === menuLabel ? null : menuLabel);
  };  // Structure des menus avec sous-menus
  const menuStructure = [
    {
      label: t('navbar.articles'),
      requireAuth: true,
      mainPath: '/articles',
      subItems: [
        { label: t('navbar.allArticles'), path: '/articles' },
        { label: t('navbar.createArticle'), path: '/articles/new' },
        { label: t('navbar.importCsv'), path: '/articles/import' },
        { label: t('navbar.importHistory'), path: '/articles/import-history' },
      ]
    },
    {
      label: t('navbar.dashboard'),
      requireAuth: true,
      subItems: [
        { label: t('navbar.quotas'), path: '/dashboard' },
        { label: t('navbar.statistics'), path: '/statistics' },
        { label: t('navbar.myActions'), path: '/actions' },
        { label: t('navbar.orders'), path: '/orders' },
        { label: t('navbar.myMasks'), path: '/mes-masques' },
        { label: t('navbar.myTags'), path: '/tags' },
        { label: t('navbar.myAutomations') || 'Mes automatisations', path: '/scheduling' },
      ]
    },
    {
      label: t('navbar.affiliation'),
      requireAuth: true,
      subItems: [
        { label: t('navbar.overview'), path: '/affiliate' },
        { label: t('navbar.referrals'), path: '/affiliate/referrals' },
        { label: t('navbar.rewards'), path: '/affiliate/rewards' },
        { label: t('navbar.myCode'), path: '/affiliate/code' },
      ]
    },
    {
      label: t('navbar.documentation'),
      requireAuth: false,
      subItems: [
        { label: t('navbar.flowdinoPrinciple'), path: '/documentation' },
        { label: t('navbar.features'), path: '/docs/features/articles-centralization' },
        { label: t('navbar.chromeExtension'), path: '/docs/chrome-installation' },
        { label: t('navbar.sites'), path: '/docs/sales-config' },
        { label: t('navbar.platforms'), path: '/docs/prestashop' },
        { label: t('navbar.affiliationDoc'), path: '/affiliate/program' },
      ]
    },
    {
      label: t('navbar.admin') || 'Administration',
      requireAuth: true,
      requireAdmin: true,
      menuRoles: ['ROLE_ADMIN_REUSSES'],
      subItems: [
        { label: t('navbar.adminDashboard') || 'Tableau de bord', path: '/admin', requiredRoles: ['ROLE_ADMIN'] },
        { label: t('navbar.adminReferrals') || 'Parrainages', path: '/admin/referrals', requiredRoles: ['ROLE_ADMIN'] },
        { label: t('navbar.adminRewards') || 'Récompenses', path: '/admin/rewards', requiredRoles: ['ROLE_ADMIN'] },
        { label: t('navbar.adminAnalytics') || 'Analytiques', path: '/admin/analytics', requiredRoles: ['ROLE_ADMIN'] },
        { label: t('navbar.adminCodes') || 'Codes d\'affiliation', path: '/admin/codes', requiredRoles: ['ROLE_ADMIN'] },
        { label: t('navbar.adminPromoCodes') || 'Codes Promo', path: '/admin/promo-codes', requiredRoles: ['ROLE_ADMIN'] },
        { label: t('navbar.adminCategories') || 'Catégories', path: '/admin/categories', requiredRoles: ['ROLE_ADMIN'] },
        { label: t('navbar.adminCategoryMapping') || 'Mapping Catégories', path: '/admin/category-mapping', requiredRoles: ['ROLE_ADMIN'] },
        { label: t('navbar.adminBrandMapping') || 'Mapping Marques', path: '/admin/brand-mapping', requiredRoles: ['ROLE_ADMIN'] },
        { label: t('navbar.adminDemarchage') || 'Démarchage', path: '/admin/demarchage', requiredRoles: ['ROLE_ADMIN'] },
        { label: t('navbar.adminAcquisition') || 'Acquisition', path: '/admin/acquisition', requiredRoles: ['ROLE_ADMIN'] },
        { label: t('navbar.adminStatsReusses') || 'Statistique Reusses', path: '/admin/stats-reusses', requiredRoles: ['ROLE_ADMIN_REUSSES'] },
      ]
    },
  ];

  // Menu Mon compte séparé
  const myAccountMenu = {
    label: t('navbar.myAccount'),
    requireAuth: true,
    subItems: [
      { label: t('navbar.profile'), path: '/profile' },
      { label: t('navbar.siteManagement'), path: '/account-sites' },
      { label: t('navbar.subscription'), path: '/subscription' },
      { label: t('navbar.paidOptions'), path: '/paid-options' },
      { label: t('navbar.billingHistory'), path: '/billing-history' },
      { label: t('navbar.logout'), action: 'logout' },
    ]
  };

  return (
    <nav className={styles.navbar}>
      <div className={styles.navbarContainer}>
        <div className={styles.logo}>
          <Link to="/">
            {/* Octobre Rose : logo rose du 1er au 31 octobre, retour automatique au logo habituel le 1er novembre */}
            {new Date().getMonth() === 9 ? (
              <OptimizedLogo
                src="/images/logo-octobre-rose.png"
                alt="FlowDino Logo Octobre Rose"
                width={50}
                height={50}
              />
            ) : (
              <OptimizedLogo
                src="/icon128.png"
                alt="FlowDino Logo"
                width={50}
                height={51}
              />
            )}
            <span className={styles.siteName}>FlowDino</span>
          </Link>
        </div>

        {/* Hamburger menu for mobile */}
        <div className={styles.mobileMenuToggle} onClick={toggleMobileMenu}>
          <span></span>
          <span></span>
          <span></span>
        </div>

        <div className={styles.navLinks}>
          {menuStructure
            .filter(menu => !menu.requireAuth || isLoggedIn)
            .filter(menu => !menu.requireAdmin || hasAnyRole(menu.menuRoles || []))
            .map((menu, index) => (
              <div key={index} className={styles.navItem}>
                {menu.mainPath ? (
                  <Link to={menu.mainPath} className={styles.navButton}>
                    {menu.label}
                    <svg
                      className={styles.chevronIcon}
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M6 9L12 15L18 9"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </Link>
                ) : (
                  <button className={styles.navButton}>
                    {menu.label}
                    <svg
                      className={styles.chevronIcon}
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M6 9L12 15L18 9"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                )}
                <div className={styles.dropdownMenu}>
                  {menu.subItems.filter(item => !item.requiredRoles || hasAnyRole(item.requiredRoles)).map((item, subIndex) => (
                    item.action === 'logout' ? (
                      <button
                        key={subIndex}
                        onClick={handleLogout}
                        className={`${styles.dropdownItem} ${styles.logoutItem}`}
                      >
                        {item.label}
                      </button>
                    ) : (
                      <Link
                        key={subIndex}
                        to={item.path}
                        className={styles.dropdownItem}
                      >
                        {item.label}
                      </Link>
                    )
                  ))}
                </div>
              </div>
            ))}
          {!isLoggedIn && (
            <div className={styles.navItem}>
              <Link to="/pricing" className={styles.navButton}>{t('navbar.pricing')}</Link>
            </div>
          )}
          {/* Blog : visible connecté ou non — c'est une page publique
              d'acquisition, pas une fonctionnalité réservée aux comptes. */}
          <div className={styles.navItem}>
            <Link to="/blog" className={styles.navButton}>{t('navbar.blog')}</Link>
          </div>
          <div className={styles.navItem}>
            <Link to="/faq" className={styles.navButton}>{t('navbar.faq')}</Link>
          </div>
        </div>

        <div className={styles.rightSection}>
          <div className={styles.authButtons}>
            {!isLoggedIn ? (
              <>
                <Link to="/login" className={styles.loginBtn}>
                  {t('navbar.login')}
                </Link>
                <Link to="/register" className={styles.registerBtn}>
                  {t('navbar.register')}
                </Link>
              </>
            ) : (
              <div className={styles.userInfo}>
                <div className={styles.userMenu}>
                  <button className={styles.userMenuButton}>
                    <div className={styles.userNameContainer}>
                      <span className={styles.userName}>
                        {`${userInfo?.firstName} ${userInfo?.lastName}`}
                      </span>
                      <span className={styles.accountLabel}>{t('navbar.myAccount')}</span>
                    </div>
                    <svg
                      className={styles.chevronIcon}
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M6 9L12 15L18 9"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  <div className={styles.dropdownMenu}>
                    {myAccountMenu.subItems.map((item, index) => (
                      item.action === 'logout' ? (
                        <button
                          key={index}
                          onClick={handleLogout}
                          className={`${styles.dropdownItem} ${styles.logoutItem}`}
                        >
                          {item.label}
                        </button>
                      ) : (
                        <Link
                          key={index}
                          to={item.path}
                          className={styles.dropdownItem}
                        >
                          {item.label}
                        </Link>
                      )
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className={styles.langSelector}>
            <button className={styles.langButton}>
              <img src={currentLang.flag} alt={currentLang.code} />
              <span>{currentLang.code}</span>
            </button>
            <div className={styles.dropdownMenu}>
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  className={styles.langOption}
                  onClick={() => handleLanguageChange(lang)}
                >
                  <img src={lang.flag} alt={lang.name} />
                  <span>{lang.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className={`${styles.mobileMenu} ${isMobileMenuOpen ? styles.show : ''}`}>
        <div className={styles.mobileNavLinks}>
          {menuStructure
            .filter(menu => !menu.requireAuth || isLoggedIn)
            .filter(menu => !menu.requireAdmin || hasAnyRole(menu.menuRoles || []))
            .map((menu, index) => (
              <div key={index} className={styles.mobileMenuItem}>
                <button
                  className={styles.mobileMenuButton}
                  onClick={() => toggleDropdown(menu.label)}
                >
                  {menu.label}
                  <svg
                    className={`${styles.chevronIcon} ${openDropdown === menu.label ? styles.rotated : ''}`}
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M6 9L12 15L18 9"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <div className={`${styles.mobileSubmenu} ${openDropdown === menu.label ? styles.show : ''}`}>
                  {menu.subItems.filter(item => !item.requiredRoles || hasAnyRole(item.requiredRoles)).map((item, subIndex) => (
                    item.action === 'logout' ? (
                      <button
                        key={subIndex}
                        onClick={() => handleMenuItemClick(item)}
                        className={`${styles.mobileSubmenuItem} ${styles.logoutItem}`}
                      >
                        {item.label}
                      </button>
                    ) : (
                      <Link
                        key={subIndex}
                        to={item.path}
                        className={styles.mobileSubmenuItem}
                        onClick={closeMobileMenu}
                      >
                        {item.label}
                      </Link>
                    )
                  ))}
                </div>
              </div>
            ))}

          {/* Blog et FAQ : pages publiques, accessibles connecté ou non */}
          <Link to="/blog" className={styles.mobilePricingLink} onClick={closeMobileMenu}>
            {t('navbar.blog')}
          </Link>
          <Link to="/faq" className={styles.mobilePricingLink} onClick={closeMobileMenu}>
            {t('navbar.faq')}
          </Link>

          {!isLoggedIn && (
            <>
              <Link to="/pricing" className={styles.mobilePricingLink} onClick={closeMobileMenu}>
                {t('navbar.pricing')}
              </Link>
              <div className={styles.mobileAuthButtons}>
                <Link to="/login" className={styles.mobileLoginBtn} onClick={closeMobileMenu}>
                  {t('navbar.login')}
                </Link>
                <Link to="/register" className={styles.mobileRegisterBtn} onClick={closeMobileMenu}>
                  {t('navbar.register')}
                </Link>
              </div>
            </>
          )}

          {isLoggedIn && (
            <div className={styles.mobileMenuItem}>
              <button
                className={styles.mobileMenuButton}
                onClick={() => toggleDropdown('myAccount')}
              >
                {t('navbar.myAccount')}
                <svg
                  className={`${styles.chevronIcon} ${openDropdown === 'myAccount' ? styles.rotated : ''}`}
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M6 9L12 15L18 9"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <div className={`${styles.mobileSubmenu} ${openDropdown === 'myAccount' ? styles.show : ''}`}>
                {myAccountMenu.subItems.map((item, subIndex) => (
                  item.action === 'logout' ? (
                    <button
                      key={subIndex}
                      onClick={() => handleMenuItemClick(item)}
                      className={`${styles.mobileSubmenuItem} ${styles.logoutItem}`}
                    >
                      {item.label}
                    </button>
                  ) : (
                    <Link
                      key={subIndex}
                      to={item.path}
                      className={styles.mobileSubmenuItem}
                      onClick={closeMobileMenu}
                    >
                      {item.label}
                    </Link>
                  )
                ))}
              </div>
            </div>
          )}

          {isLoggedIn && (
            <div className={styles.mobileUserInfo}>
              <span className={styles.mobileUserName}>
                {`${userInfo?.firstName} ${userInfo?.lastName}`}
              </span>
            </div>
          )}

          <div className={styles.mobileLangOptions}>
            <h3 className={styles.mobileMenuSectionTitle}>{t('navbar.selectLanguage')}</h3>
            {languages.map((lang) => (
              <button
                key={lang.code}
                className={`${styles.mobileLangOption} ${
                  currentLang.code === lang.code ? styles.mobileLangOptionActive : ''
                }`}
                onClick={() => {
                  handleLanguageChange(lang);
                  closeMobileMenu();
                }}
              >
                <img src={lang.flag} alt={lang.name} />
                <span>{lang.name}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
